import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Truck, Save, Package, TrendingUp, RefreshCcw, Key } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminEnvios() {
    const [tab, setTab] = useState('settings');
    const [settings, setSettings] = useState(null);
    const [baseUrl, setBaseUrl] = useState('');
    const [tokenInput, setTokenInput] = useState('');
    const [stats, setStats] = useState(null);
    const [shipments, setShipments] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [{ data: s }, { data: st }, { data: sh }] = await Promise.all([
                api.get('/admin/envios/settings'),
                api.get('/admin/envios/stats'),
                api.get('/admin/envios/shipments'),
            ]);
            setSettings(s);
            setBaseUrl(s.base_url || '');
            setStats(st);
            setShipments(sh || []);
        } catch { toast.error('Error al cargar'); }
    };

    useEffect(() => { load(); }, []);

    const saveSettings = async () => {
        setBusy(true);
        try {
            const payload = { base_url: baseUrl };
            if (tokenInput) payload.token = tokenInput;
            await api.put('/admin/envios/settings', payload);
            toast.success('Configuración guardada');
            setTokenInput('');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="admin-envios-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Truck className="h-6 w-6 text-[hsl(var(--primary))]" />
                    <h1 className="text-2xl font-semibold">OkEnvíos (envia.com)</h1>
                    {settings?.has_token ? (
                        <Badge className="bg-emerald-100 text-emerald-800">Configurado · {settings.token_source}</Badge>
                    ) : (
                        <Badge variant="outline" className="border-destructive text-destructive">Sin token</Badge>
                    )}
                </div>
                <Button variant="outline" size="sm" onClick={load}><RefreshCcw className="h-4 w-4 mr-1" />Refrescar</Button>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="settings" data-testid="envios-settings-tab"><Key className="h-3.5 w-3.5 mr-1" />Configuración</TabsTrigger>
                    <TabsTrigger value="stats" data-testid="envios-stats-tab"><TrendingUp className="h-3.5 w-3.5 mr-1" />Estadísticas</TabsTrigger>
                    <TabsTrigger value="shipments" data-testid="envios-shipments-tab"><Package className="h-3.5 w-3.5 mr-1" />Guías ({shipments.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="settings" className="pt-4">
                    <Card><CardContent className="p-5 space-y-4 max-w-xl">
                        <div>
                            <Label>Base URL</Label>
                            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.envia.com" data-testid="envios-base-url" />
                            <p className="text-xs text-muted-foreground mt-1">Sandbox: <code>https://api-test.envia.com</code> · Producción: <code>https://api.envia.com</code></p>
                        </div>
                        <div>
                            <Label>API Token (Bearer)</Label>
                            <Input type="password" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} placeholder={settings?.token_masked ? `Actual: ${settings.token_masked} (deja vacío para conservar)` : 'Pega tu token de envia.com'} data-testid="envios-token" />
                            <p className="text-xs text-muted-foreground mt-1">Fuente actual: <strong>{settings?.token_source || 'ninguna'}</strong>. La clave se guarda cifrada del lado backend.</p>
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={saveSettings} disabled={busy} data-testid="envios-save-settings"><Save className="h-4 w-4 mr-1" />{busy ? 'Guardando…' : 'Guardar'}</Button>
                        </div>
                    </CardContent></Card>
                </TabsContent>

                <TabsContent value="stats" className="pt-4">
                    {!stats ? (
                        <div className="text-sm text-muted-foreground text-center py-10">Cargando…</div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <Card><CardContent className="p-4">
                                    <div className="text-xs uppercase text-muted-foreground">Total guías</div>
                                    <div className="text-3xl font-bold mt-1" data-testid="envios-total">{stats.total_shipments}</div>
                                </CardContent></Card>
                                <Card><CardContent className="p-4">
                                    <div className="text-xs uppercase text-muted-foreground">Activas</div>
                                    <div className="text-3xl font-bold text-emerald-700 mt-1">{stats.active}</div>
                                </CardContent></Card>
                                <Card><CardContent className="p-4">
                                    <div className="text-xs uppercase text-muted-foreground">Canceladas</div>
                                    <div className="text-3xl font-bold text-destructive mt-1">{stats.cancelled}</div>
                                </CardContent></Card>
                                <Card><CardContent className="p-4">
                                    <div className="text-xs uppercase text-muted-foreground">Ingreso total</div>
                                    <div className="text-3xl font-bold mt-1">${fmt(stats.revenue_mxn)}</div>
                                </CardContent></Card>
                            </div>
                            <Card><CardContent className="p-4">
                                <h3 className="text-sm font-semibold mb-3">Por transportista</h3>
                                <table className="w-full text-sm">
                                    <thead><tr className="text-xs uppercase text-muted-foreground border-b"><th className="text-left py-2">Carrier</th><th className="text-right">Guías</th><th className="text-right">Ingreso</th></tr></thead>
                                    <tbody>
                                        {(stats.by_carrier || []).length === 0 && <tr><td colSpan="3" className="py-4 text-center text-muted-foreground">Aún sin datos.</td></tr>}
                                        {(stats.by_carrier || []).map(r => (
                                            <tr key={r.carrier} className="border-b last:border-0">
                                                <td className="py-2 font-medium uppercase">{r.carrier}</td>
                                                <td className="text-right">{r.count}</td>
                                                <td className="text-right font-mono">${fmt(r.revenue)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent></Card>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="shipments" className="pt-4">
                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
                                <tr><th className="p-2 text-left">Tracking</th><th className="text-left">Usuario</th><th className="text-left">Carrier</th><th className="text-left">Ruta</th><th className="text-right">Costo</th><th>Estado</th><th></th></tr>
                            </thead>
                            <tbody>
                                {shipments.length === 0 && <tr><td colSpan="7" className="p-4 text-center text-muted-foreground">Sin guías creadas todavía.</td></tr>}
                                {shipments.map(s => (
                                    <tr key={s.id} className="border-b last:border-0 hover:bg-slate-50">
                                        <td className="p-2 font-mono text-xs">{s.trackingNumber || '—'}</td>
                                        <td>@{s.user_nickname || '?'}</td>
                                        <td className="uppercase">{s.carrier}</td>
                                        <td className="text-xs">{s.origin_zip} → {s.destination_zip}</td>
                                        <td className="text-right">${fmt(s.totalPrice)}</td>
                                        <td>{s.status === 'cancelled' ? <Badge variant="outline" className="border-destructive text-destructive">cancelada</Badge> : <Badge className="bg-emerald-100 text-emerald-800">activa</Badge>}</td>
                                        <td className="text-right pr-2">{s.labelUrl && <a className="text-xs text-primary underline" href={s.labelUrl} target="_blank" rel="noreferrer">Guía</a>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
